import localhost from "apis/localhost";
import { showErrorModal } from "slices/app/actions/error";
import { startSSEConnection } from "slices/workspace/actions/uploadFiles";
import {
  initFileUploadStates,
  setCurrentFlowId,
} from "slices/workspace/foldersSlice";
import { selectFiles } from "slices/workspace/selectors";

import {
  buildBasePayload,
  createLoadingController,
  finalizeUpload,
} from "./pipeline";
import { getStrategy } from "./strategies";

const START_PROCESSING_URL = "/document/queue/stream/start-processing";
const NOTHING_TO_PROCESS_DELAY = 1000;

const postInitialize = async (url, payload) => {
  const response = await localhost.post(url, payload);
  if (response.status !== 200) {
    throw new Error("Failed to initialize upload session");
  }
  return response.data;
};

// The backend occasionally drops the first trigger; a single silent retry
// mirrors the pre-refactor behaviour. Returns false when both attempts fail.
const startProcessing = async (flowId) => {
  try {
    await localhost.post(START_PROCESSING_URL, { flowId });
    return true;
  } catch (error) {
    try {
      await localhost.post(START_PROCESSING_URL, { flowId });
      return true;
    } catch (retryError) {
      return false;
    }
  }
};

const runFlow = async ({
  strategy,
  data,
  dispatch,
  metadata,
  files,
  stopLoading,
}) => {
  const { flowId } = data;

  dispatch(setCurrentFlowId(flowId));
  await dispatch(startSSEConnection(flowId));

  if (!(await startProcessing(flowId))) {
    stopLoading();
    return null;
  }

  if (
    data?.filesToBeProcessed?.length === 0 &&
    data?.filesToBeUploadedFromLocal?.length === 0
  ) {
    setTimeout(stopLoading, NOTHING_TO_PROCESS_DELAY);
    return null;
  }

  return strategy.resolveInitResponse({ data, dispatch, metadata, files, flowId });
};

export const createHandleContinue =
  ({ context }) =>
  (target, onFilesUploaded) =>
  async (dispatch, getState) => {
    const loading = createLoadingController({
      dispatch,
      selectedCollection: context.selectedCollection,
    });
    loading.start();

    try {
      const strategy = getStrategy(target);

      const selectedFiles = selectFiles(getState());
      const files = strategy.prepareFiles({
        selectedFiles,
        selectedCollection: context.selectedCollection,
      });

      const state = getState();
      const payload = buildBasePayload(
        files,
        strategy.buildBaseInfo(files, context)
      );
      const fields = strategy.getFields(state);
      Object.assign(payload, fields);
      const metadata = { ...fields };

      await strategy.beforeInitialize({
        payload,
        state,
        dispatch,
        selectedCollection: context.selectedCollection,
        files,
      });

      dispatch(initFileUploadStates(files));

      const data = await postInitialize(strategy.uploadUrl, payload);

      const result = strategy.usesFlow
        ? await runFlow({
            strategy,
            data,
            dispatch,
            metadata,
            files,
            stopLoading: loading.stop,
          })
        : strategy.resolveDirectResponse({ data, files });

      if (!result) {
        return;
      }

      await finalizeUpload({
        dispatch,
        stopLoading: loading.stop,
        target,
        onFilesUploaded,
        metadata,
        ...result,
      });

      strategy.afterUpload?.({ ...result, dispatch });
    } catch (error) {
      // Guarantee the loading indicator is dismissed if any step blows up
      // (e.g. /rag/collection/reindex 5xx when the RAG service is unreachable),
      // otherwise the user is stuck on a permanent spinner.
      loading.stop();
      dispatch(showErrorModal(error));
      throw error;
    }
  };
